import { token } from '@bynder/design-system';

export const GlobalStyles = () => (
  <style>
    {`
:root {
  /* cast token values to CSS variables so we can use these in our TEA CSS files natively */
  --tea-blue-500: ${token.blue500};
  --tea-gray-25: ${token.gray25};
  --tea-gray-100: ${token.gray100};
  --tea-gray-400: ${token.gray400};
  --tea-gray-600: ${token.gray600a};
  --tea-gray-800: ${token.gray800};
  --tea-spacing-3: ${token.spacing3};
  --tea-spacing-4: ${token.spacing4};
  --tea-spacing-5: ${token.spacing5};
}

.visually-hidden {
    position: absolute;
    overflow: hidden;
    /*
    Clipping that one pixel means it will be completely invisible
    */
    clip-path: inset(50%);
    /*
    1px necessary as Safari apparently will make 0 x 0 elements unfocusable - we need focus in some cases if we use a class like this to hide content
    */
    width: 1px;
    height: 1px;
    white-space: nowrap;
}

/*
For some reason, this menu exists outside of the TEA wrapper
*/
.cell-color-picker  {
    :has([role="option"]) {
      padding: var(--tea-spacing-3) var(--tea-spacing-4);
    }
    .colour-name {
      font-size: 16px;
    }
    .subtext {
      color: var(--tea-gray-600);
    }
}


/*
Styles scoped to TEA widget
*/

.tea-wrapper {
  container-type: inline-size;
  container-name: teaEditor;
  display: flex;
  flex-direction: column;
  position: relative;

  .ProseMirror-gapcursor {
  /*
   * We get most of the styles we need from the gapcursor extension, but they show a horizontal line
   * these overrides convert it to a vertical one
  */
  position: relative;
  height: 18px;
  margin-top: 1rem;
  margin-bottom: 1rem;
    &:after {
      display: block;
      height: 18px;
      border-left: 1px solid black;
      border-top: 0;
    }
  }

  .tea-editor-shortcut-message {
    width: 100%;
    position: absolute;
    bottom: -60px;
    color: var(--tea-gray-400);
    text-align: right;
    padding-right: .5rem;
  }


  /*
  TEA formats toolbar
  */

  .tea-formats-toolbar {
    align-self: center;
    display: flex;
    position: absolute;
    z-index: 50;

    &:focus {
      outline: var(--tea-gray-600) solid 2px;
    }

    > div:first-child {
      flex-wrap: nowrap;
      width: 100%;
    }

    > div {
      margin: 0;
    }
  }

  @container teaEditor (max-width: 800px) {
    .tea-formats-toolbar {
      > div:first-child {
        flex-wrap: wrap;
      }

      div[class*="DividerContainer"] {
        display: none;
      }
    }
  }

  .tea-table-toolbar {
    position: absolute;
    z-index: 1;
    display: none;
    margin: 0;
    padding: 0;
    width: 0;

    div {
      box-shadow: none;
      margin: 0;
      padding: 0;
    }

    button {
      padding: 0;
    }
  }

  .tea-editor,
  .tea-read-only {
    /* Style the pre tag specifically
    * applying to the actual code element casues issues when the text wraps in the code block
    * such as the background blocking the text on the line above
    */
    pre:has(code) {
      background-color: var(--tea-gray-800);
      color: #ffffff;
      padding: 0.5em;
      border-radius: 0.25em;
    }

    [data-anchored] {
      background: none;
      border: none;
      cursor: text;
      padding: 0;
      margin: 0;
    }

    code:not(pre code) {
      background-color: var(--tea-gray-800);
      color: #ffffff;
    }

    /*
    * Tables in editor
    */
    table {
      border-collapse: collapse;
      border-spacing: 0;
      width: 100%;
    }

    td,
    th {
      border: 1px solid #ddd;
      padding: 0 8px;
      position: relative;
      word-break: break-word;
      min-width: 100px;
    }

    td p {
    margin-top: 1em;
    margin-bottom: 1em;
    }

    th {
      background-color: #f7f7f7;
    }

    .selectedCell {
      background-color: var(--tea-gray-25);
      outline: 2px solid var(--tea-blue-500);
    }

    /*
    * All other styles
    */
    ul,
    ol {
      padding-left: 40px;
    }

    ul li {
      list-style: disc !important;
    }

    ol li {
      list-style: decimal !important;
    }

    a {
      color: var(--tea-blue-500);
      text-decoration: underline;
    }

    /* CXUC-specific styles */
    h1, h2, h3, h4, h5, h6, p, table {
      strong, b, em, i, u, s, a, sup, sub {
        font-family: inherit !important;
      }
    }

    /*
     * Style bullet and list item markers
    */
    li[style*="--marker-color"]::marker {
      color: var(--marker-color);
    }
  }

  .tea-read-only {
    [data-anchored] {
      cursor: pointer;
      display: inline-block;
    }

    h1, h2, h3, h4, h5, h6 {
      button {
        background: none;
        border: none;
        color: inherit;
        font-family: inherit;
        text-align: inherit;
        font-size: 100%;
        margin: 0;
        outline: none;
        overflow: visible;
        padding: 0;
        text-transform: none;
      }

      button:focus {
        background: rgba(51,78,92,0.05)!important;
      }
    }

    /* Tiptap populates empty paragraphs with <br/>s in edit mode
    but strips them out of the output.
    This ensures that empty paragraphs are treated like line breaks
    in read-only mode, i.e. they have layout and take up visual
    space in the DOM as intended. */
    p:empty::before {
      content: '';
      display: inline-block;
    }
  }

  .tiptap p.is-editor-empty:first-child::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
}


/**
AI tool styles
**/
.response-container {
  border: 1px solid var(--tea-gray-100);
  border-radius: 5px;
  padding: var(--tea-spacing-3) var(--tea-spacing-5);
}

#validation-error-msg {
 color: red;
}

.ai-prompt-flex-wrapper > div {
  display:flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 0;
}

.ai-prompt-button-flex-wrapper {
  padding-top: 1rem;
}


 .modal-link {
      color: var(--tea-gray-600);
      text-decoration: none;
}

.modal-button-group {
  :nth-child(2) {
    margin-left: 12px;
  }
}

`}
  </style>
);
